import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom'
import React from 'react';
import { decodeJWT } from './Components/jwtUtils';
import Api from './Auth/Api';

//Auth
import useAuth from "./Auth/hook_useAuth";
import { setupInterceptors } from './Auth/Api';

//Context
import { AjustesProvider, useAjustes } from './Context/AjustesContext';

//Components
import LoadingScreen from './Components/LoadingScreen';
import CustomScrollbar from './Components/CustomScrollbar';

//Layout
import Header from './Layout/Header';
import Content from './Components/Content';
import Sidebar from './Layout/Sidebar';
import ContainerFlex from './Components/ContainerFlex';
import ContainerBox from './Components/ContainerBox';

//Paginas
import Dashboard from './Pages/Dashboard';
import ListaAlunos from './Pages/ListaAlunos';
import MeusCursos from './Pages/MeusCursos';
import Calendario from './Pages/Calendario';
import Questionario from './Pages/Questionario';
import Modulos from './Pages/Modulos';
import Aulas from './Pages/Aulas';
import Aula from './Pages/Aula';
import Certificados from './Pages/Certificados';
import Biblioteca from './Pages/Biblioteca';
import Configuracoes from './Pages/Configuracoes';
import Chat from './Pages/Chat';
import Login from './Pages/Login';
import Provas from './Pages/Provas';
import SemPermissao from './Pages/SemPermissao';
import ListaUsuarios from './Pages/ListaUsuarios';
import InformacoesUsuario from './Pages/InformacoesUsuario';
import Recuperar from './Pages/Recuperar';
import PrimeiroLogin from './Pages/PrimeiroLogin';
import Topicos from './Pages/Topicos';
import TrocarSenha from './Pages/TrocarSenha';
import AulaGrupo from './Pages/AulaGrupo';
import RelatorioGrupo from './Pages/Relatorios/Grupo';
import RelatorioAcompanhamento from './Pages/Relatorios/Acompanhamento';
import RelatorioAluno from './Pages/Relatorios/Aluno';
import PesquisaSatisfacao from './Pages/PesquisaSatisfacao';
import RelatorioFeedback from './Pages/Relatorios/Feedback';
import RelatorioAvaliacaoReacao from './Pages/Relatorios/AvaliacaoReacao';
import RelatorioAvaliacaoReacaoHibrido from './Pages/Relatorios/AvaliacaoReacaoHibrido';
import AgendamentosTreinamentos from './Pages/AgendamentosTreinamentos';
import RelatorioAvaliacaoEficacia from './Pages/Relatorios/AvaliacaoEficacia';
import Ajustes from './Pages/Ajustes';
import Validador from './Pages/Validador';
import Termos from './Pages/Termos';
import ListaEmpresas from './Pages/ListaEmpresas';
import DashboardEmpresa from './Pages/DashboardEmpresa';
import ConfiguracoesGlobais from './Pages/ConfiguracoesGlobais';
import SuperAdmins from './Pages/SuperAdmins';
import CatalogoCursos from './Pages/CatalogoCursos';

import { SPA_PREVIOUS_INTERNAL_PATH_KEY } from './utils/spaPreviousNavigation';

//Paginas Aluno
import DashboardAluno from './Pages/Aluno/DashboardAluno';
import Certificado from './Pages/Aluno/Certificado';
import HistoricoEscolar from './Pages/HistoricoEscolar';
import HistoricoEscolarAluno from './Pages/Aluno/HistoricoEscolarAluno';

//Paginas Gestor
import Resetar from './Pages/Resetar';

// Novo import para o componente QuestionarioReacao
import QuestionarioReacao from './Pages/QuestionarioReacao';
// Adicionar o componente de questionário de reação híbrido
import QuestionarioReacaoHibrido from './Pages/QuestionarioReacaoHibrido';
import QuestionarioEficacia from './Pages/QuestionarioEficacia';

function MultiRoleRoute({ allowedRoles, children }) {
    const token = JSON.parse(localStorage.getItem("user_token"));
    const primeiro_login = localStorage.getItem("primeiro_login");
    const nivel = decodeJWT(token).nivel;
    const currentPath = window.location.pathname;
    const [verificandoPesquisa, setVerificandoPesquisa] = React.useState(true);
    const [precisaResponderPesquisa, setPrecisaResponderPesquisa] = React.useState(false);
    
    // Verificar pesquisa de satisfação anual se não for Administrador e já passou pelo primeiro login
    React.useEffect(() => {
        const verificarPesquisaAnual = async () => {
            if (nivel !== 'Administrador' && primeiro_login === '2') {
                try {
                    const response = await Api.get('/api/verificar-pesquisa-satisfacao-ano-atual', {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    });
                    
                    // Verificar se deve mostrar a pesquisa: não respondeu E deve mostrar (50% de chance + 3 aulas)
                    if (!response.data.respondido && response.data.deveMostrar && currentPath !== '/pesquisa-satisfacao') {
                        setPrecisaResponderPesquisa(true);
                        window.location.href = '/pesquisa-satisfacao';
                    }
                } catch (error) {
                    console.error("Erro ao verificar pesquisa de satisfação:", error);
                } finally {
                    setVerificandoPesquisa(false);
                }
            } else {
                setVerificandoPesquisa(false);
            }
        };
        
        verificarPesquisaAnual();
    }, [nivel, primeiro_login, currentPath, token]);
    
    // Verifica se está na rota correta para primeiro login
    if ((primeiro_login === '0' || primeiro_login === 'null') && currentPath !== '/primeiro-login') {
        window.location.href = '/primeiro-login';
        return null;
    }

    // Verifica se está na rota correta para trocar senha
    if (primeiro_login === '1' && currentPath !== '/trocar-senha') {
        window.location.href = '/trocar-senha';
        return null;
    }

    if (verificandoPesquisa) {
        return null; // Aguardar verificação
    }

    if (precisaResponderPesquisa && currentPath !== '/pesquisa-satisfacao') {
        return null; // Redirecionamento em andamento
    }

    return allowedRoles.includes(nivel) ? children : <SemPermissao />;
}

/** Rota restrita ao Super Admin (claim super_admin no token ou nível 'Super Admin'). */
function SuperAdminRoute({ children }) {
    const token = JSON.parse(localStorage.getItem("user_token"));
    const claims = decodeJWT(token) || {};
    const ehSuperAdmin = claims.super_admin === true || claims.nivel === 'Super Admin';
    return ehSuperAdmin ? children : <SemPermissao />;
}

function RouteChangeListener() {
    const location = useLocation();

    React.useEffect(() => {
        const lastAccess = localStorage.getItem('ultimo_acesso');
        const userToken = localStorage.getItem('user_token');
        const now = new Date().toISOString();

        if (lastAccess && userToken) {
            const lastAccessDate = new Date(lastAccess);
            const nowDate = new Date(now);
            const diffInSeconds = (nowDate - lastAccessDate) / 1000;

            if (diffInSeconds > process.env.REACT_APP_TOKEN_TEMPO_EXPIRACAO) {
                localStorage.clear();
                window.location.href = "/login";
            }
        }
        if (userToken) {
            localStorage.setItem('ultimo_acesso', now);
        }
    }, [location]);

    return null;
}

/** Grava em sessionStorage a rota interna imediatamente anterior (pathname + search). Usado pelo Voltar em Modulos/Aulas. */
function SpaPreviousRouteTracker() {
    const location = useLocation();
    const prevFullPathRef = React.useRef(null);

    React.useEffect(() => {
        const full = `${location.pathname}${location.search}`;
        if (prevFullPathRef.current !== null) {
            try {
                sessionStorage.setItem(SPA_PREVIOUS_INTERNAL_PATH_KEY, prevFullPathRef.current);
            } catch {
                /* ignore */
            }
        }
        prevFullPathRef.current = full;
    }, [location.pathname, location.search]);

    return null;
}

function AuthenticatedRoutes() {
    const token = JSON.parse(localStorage.getItem("user_token"));
    const claims = decodeJWT(token) || {};
    const nivel = claims.nivel;
    const ehSuperAdmin = claims.super_admin === true || nivel === 'Super Admin';
    const semEmpresaSelecionada = !claims.empresa_id;
    // Super Admin editando o catálogo de cursos (empresa reservada): a home vira a lista de cursos
    const modoCatalogo = ehSuperAdmin && claims.catalogo === true;

    // Super Admin sem empresa selecionada: layout idêntico ao normal (com Sidebar).
    if (ehSuperAdmin && semEmpresaSelecionada) {
        return (
            <Router>
                <RouteChangeListener />
                <SpaPreviousRouteTracker />
                <CustomScrollbar />
                <Content>
                    <Header />
                    <ContainerFlex>
                        <Sidebar />
                        <ContainerBox>
                            <Routes>
                                <Route path="/listar-empresas" element={<ListaEmpresas />} />
                                <Route path="/catalogo" element={<CatalogoCursos />} />
                                <Route path="/empresa/:id/dashboard" element={<DashboardEmpresa />} />
                                <Route path="/configuracoes-globais" element={<ConfiguracoesGlobais />} />
                                <Route path="/super-admins" element={<SuperAdmins />} />
                                <Route path="/configuracoes" element={<Configuracoes />} />
                                <Route path="*" element={<DashboardEmpresa />} />
                            </Routes>
                        </ContainerBox>
                    </ContainerFlex>
                </Content>
            </Router>
        );
    }

    return (
        <Router>
            <RouteChangeListener />
            <SpaPreviousRouteTracker />
            <CustomScrollbar />
            <Content>
                <Header />
                <ContainerFlex>
                    <Sidebar />
                    <ContainerBox>
                        <Routes>
                            <Route path="*" element={modoCatalogo ? <MeusCursos /> : (nivel === "Aluno" ? <DashboardAluno /> : <Dashboard />)} />
                            <Route path="/primeiro-login" element={<PrimeiroLogin />} />
                            <Route path="/trocar-senha" element={<TrocarSenha />} />

                            <Route path="/listar-alunos/:id" element={<MultiRoleRoute allowedRoles={['Aluno', 'Gestor', 'Administrador', 'RH']}><ListaAlunos /></MultiRoleRoute>} />
                            <Route path="/meus-cursos" element={<MultiRoleRoute allowedRoles={['Aluno', 'Gestor', 'Gestor', 'Administrador', 'RH']}><MeusCursos /></MultiRoleRoute>} />
                            <Route path="/calendario" element={<MultiRoleRoute allowedRoles={['Aluno', 'Gestor', 'Administrador', 'RH']}><Calendario /></MultiRoleRoute>} />
                            <Route path="/questionario/:id" element={<MultiRoleRoute allowedRoles={['Aluno', 'Gestor', 'Administrador', 'RH']}><Questionario /></MultiRoleRoute>} />
                            <Route path="/questionario/p2/:id" element={<MultiRoleRoute allowedRoles={['Aluno', 'Gestor', 'Administrador', 'RH']}><Questionario /></MultiRoleRoute>} />
                            <Route path="/questionario/curso/:id" element={<MultiRoleRoute allowedRoles={['Aluno', 'Gestor', 'Administrador', 'RH']}><Questionario /></MultiRoleRoute>} />
                            <Route path="/questionario/extra/:id" element={<MultiRoleRoute allowedRoles={['Aluno', 'Gestor', 'Administrador', 'RH']}><Questionario /></MultiRoleRoute>} />
                            <Route path="/questionario-reacao/:id" element={<MultiRoleRoute allowedRoles={['Aluno', 'Gestor', 'Administrador', 'RH']}><QuestionarioReacao /></MultiRoleRoute>} />
                            <Route path="/questionario/reacao/:id" element={<MultiRoleRoute allowedRoles={['Aluno', 'Gestor', 'Administrador', 'RH']}><QuestionarioReacao /></MultiRoleRoute>} />
                            <Route path="/questionario-reacao-hibrido/:id" element={<MultiRoleRoute allowedRoles={['Aluno', 'Gestor', 'Administrador', 'RH']}><QuestionarioReacaoHibrido /></MultiRoleRoute>} />
                            <Route path="/questionario/reacao-hibrido/:id" element={<MultiRoleRoute allowedRoles={['Aluno', 'Gestor', 'Administrador', 'RH']}><QuestionarioReacaoHibrido /></MultiRoleRoute>} />
                            <Route path="/avaliacao-eficacia/:id" element={<MultiRoleRoute allowedRoles={['Gestor', 'Administrador', 'RH']}><QuestionarioEficacia /></MultiRoleRoute>} />
                            <Route path="/modulos/:id" element={<MultiRoleRoute allowedRoles={['Aluno', 'Gestor', 'Administrador', 'RH']}><Modulos /></MultiRoleRoute>} />
                            <Route path="/aulas/:id" element={<MultiRoleRoute allowedRoles={['Aluno', 'Gestor', 'Administrador', 'RH']}><Aulas /></MultiRoleRoute>} />
                            <Route path="/aula/:id" element={<MultiRoleRoute allowedRoles={['Aluno', 'Gestor', 'Administrador', 'RH']}><Aula /></MultiRoleRoute>} />
                            <Route path="/configuracoes" element={<MultiRoleRoute allowedRoles={['Aluno', 'Gestor', 'Administrador', 'RH']}><Configuracoes /></MultiRoleRoute>} />
                            <Route path="/biblioteca" element={<MultiRoleRoute allowedRoles={['Aluno', 'Gestor', 'Administrador', 'RH']}><Biblioteca /></MultiRoleRoute>} />
                            <Route path="/provas-extras" element={<MultiRoleRoute allowedRoles={['Aluno', 'Gestor', 'Administrador', 'RH']}><Provas /></MultiRoleRoute>} />
                            <Route path="/chat" element={<MultiRoleRoute allowedRoles={['Aluno', 'Gestor', 'Administrador', 'RH']}><Chat /></MultiRoleRoute>} />
                            <Route path="/pesquisa-satisfacao" element={<MultiRoleRoute allowedRoles={['Aluno', 'Gestor', 'Administrador', 'RH']}><PesquisaSatisfacao /></MultiRoleRoute>} />

                            {/* Rotas só de Admin */}
                            <Route path="/listar-topicos" element={<MultiRoleRoute allowedRoles={['Administrador', 'RH']}><Topicos /></MultiRoleRoute>} />
                            <Route path="/relatorios/feedback" element={<MultiRoleRoute allowedRoles={['Administrador', 'RH']}><RelatorioFeedback /></MultiRoleRoute>} />
                            <Route path="/relatorios/avaliacao-reacao" element={<MultiRoleRoute allowedRoles={['Administrador', 'RH']}><RelatorioAvaliacaoReacao /></MultiRoleRoute>} />
                            <Route path="/relatorios/avaliacao-reacao-hibrido" element={<MultiRoleRoute allowedRoles={['Administrador', 'RH']}><RelatorioAvaliacaoReacaoHibrido /></MultiRoleRoute>} />
                            <Route path="/relatorios/avaliacao-eficacia" element={<MultiRoleRoute allowedRoles={['Administrador', 'RH']}><RelatorioAvaliacaoEficacia /></MultiRoleRoute>} />
                            <Route path="/ajustes" element={<MultiRoleRoute allowedRoles={['Administrador']}><Ajustes /></MultiRoleRoute>} />
                            <Route path="/termos" element={<MultiRoleRoute allowedRoles={['Administrador', 'RH']}><Termos /></MultiRoleRoute>} />

                            {/* Super Admin: gestão de empresas e troca de contexto */}
                            <Route path="/listar-empresas" element={<SuperAdminRoute><ListaEmpresas /></SuperAdminRoute>} />
                            <Route path="/empresa/:id/dashboard" element={<SuperAdminRoute><DashboardEmpresa /></SuperAdminRoute>} />
                            <Route path="/configuracoes-globais" element={<SuperAdminRoute><ConfiguracoesGlobais /></SuperAdminRoute>} />
                            <Route path="/super-admins" element={<SuperAdminRoute><SuperAdmins /></SuperAdminRoute>} />

                            {/* Rotas só de Aluno */}
                            <Route path="/aluno/dashboard" element={<MultiRoleRoute allowedRoles={['Aluno']}><DashboardAluno /></MultiRoleRoute>} />
                            <Route path="/aluno/certificado" element={<MultiRoleRoute allowedRoles={['Aluno']}><Certificado /></MultiRoleRoute>} />
                            <Route path="/aluno/historico-escolar" element={<MultiRoleRoute allowedRoles={['Aluno']}><HistoricoEscolarAluno /></MultiRoleRoute>} />

                            {/* Rotas Gestor e Admin */}
                            <Route path="/relatorios/acompanhamento" element={<MultiRoleRoute allowedRoles={['Gestor', 'Administrador', 'RH']}><RelatorioAcompanhamento /></MultiRoleRoute>} />
                            <Route path="/relatorios/grupo" element={<MultiRoleRoute allowedRoles={['Gestor', 'Administrador', 'RH']}><RelatorioGrupo /></MultiRoleRoute>} />
                            <Route path="/relatorios/aluno" element={<MultiRoleRoute allowedRoles={['Gestor', 'Administrador', 'RH']}><RelatorioAluno /></MultiRoleRoute>} />
                            <Route path="/aula-grupo/:id" element={<MultiRoleRoute allowedRoles={['Gestor', 'Administrador', 'RH']}><AulaGrupo /></MultiRoleRoute>} />
                            <Route path="/dashboard" element={modoCatalogo ? <Navigate to="/meus-cursos" replace /> : <MultiRoleRoute allowedRoles={['Gestor', 'Administrador', 'RH']}><Dashboard /></MultiRoleRoute>} />
                            <Route path="/listar-usuarios" element={<MultiRoleRoute allowedRoles={['Gestor', 'Administrador', 'RH']}><ListaUsuarios /></MultiRoleRoute>} />
                            <Route path="/usuarios/informacoes/:id" element={<MultiRoleRoute allowedRoles={['Gestor', 'Administrador', 'RH']}><InformacoesUsuario /></MultiRoleRoute>} />
                            <Route path="/certificados" element={<MultiRoleRoute allowedRoles={['Gestor', 'Administrador', 'RH']}><Certificados /></MultiRoleRoute>} />
                            <Route path="/historico-escolar" element={<MultiRoleRoute allowedRoles={['Gestor', 'Administrador', 'RH']}><HistoricoEscolar /></MultiRoleRoute>} />
                            <Route path="/agendamentos-treinamentos" element={<MultiRoleRoute allowedRoles={['Gestor', 'Administrador', 'RH']}><AgendamentosTreinamentos /></MultiRoleRoute>} />

                        </Routes>
                    </ContainerBox>
                </ContainerFlex>
            </Content>
        </Router>
    )
}

function NotAthenticatedRoutes() {
    return (
        <Router>
            <Routes>
                <Route path="*" element={<Login />} />
                <Route path="/recuperar" element={<Recuperar />} />
                <Route path="/resetar" element={<Resetar />} />
                <Route path="/validador" element={<Validador />} />
            </Routes>
        </Router>
    )
}

function AppContent() {
    const { user, loading: authLoading } = useAuth();
    const { loading: ajustesLoading } = useAjustes();
    
    // Se estiver carregando autenticação ou ajustes, mostra a tela de loading
    if (authLoading || ajustesLoading) {
        return <LoadingScreen message="Carregando a plataforma..." />;
    }
    
    const user_token = localStorage.getItem("user_token");
    if (user_token === 'undefined') {
        localStorage.clear();
    }
    
    return user ? <AuthenticatedRoutes /> : <NotAthenticatedRoutes />;
}

function App() {
    React.useEffect(() => {
        setupInterceptors();
    }, []);

    return (
        <AjustesProvider>
            <AppContent />
        </AjustesProvider>
    );
}

export default App;
